import {
  Box,
  IconButton,
  InputAdornment,
  Popover,
  TextField,
  Theme,
} from '@mui/material';
import { SxProps } from '@mui/system';
import React, {
  ChangeEvent,
  ClipboardEvent,
  FC,
  MouseEvent,
  ReactNode,
  useCallback,
  useState,
} from 'react';
import { ColorPicker, ColorService, IColor } from 'react-color-palette';
import 'react-color-palette/css';
import { hexRegex, rgbRgbaRegex } from 'src/constants/validations';

type ColorFormat = "hex" | "rgb";

function detectColorFormat(color: string | null | undefined): ColorFormat {
  if (color) {
    if (hexRegex.test(color)) {
      return "hex";
    }

    if (rgbRgbaRegex.test(color)) {
      return "rgb";
    }
  }

  return "hex";
}

const emptyColor: Record<ColorFormat, string> = {
  hex: "#FFFFFF",
  rgb: "rgba(255, 255, 255)",
};

function formatStringToIColor<F extends ColorFormat>(colorFormat: F, color: string | null | undefined): IColor {
  if (colorFormat === "hex") {
    return ColorService.convert(colorFormat, ColorService.toHex(color || emptyColor[colorFormat]) as IColor[F]);
  }

  return ColorService.convert(colorFormat, ColorService.toRgb(color || emptyColor[colorFormat]) as IColor[F]);
}

function formatIColorToString<F extends ColorFormat>(colorFormat: F, color: IColor): string {
  const { hex, rgb } = color;

  if (colorFormat === "hex") {
    return hex;
  }

  return `rgba(${rgb.r?.toFixed(0)}, ${rgb.g?.toFixed(0)}, ${rgb.b?.toFixed(0)}, ${
    rgb.a?.toFixed(2).replace(/\.?0+$/, "") || 0
  })`;
}

type ColorInputProps = {
  label?: ReactNode;
  placeholder?: string;
  value: string | null | undefined;
  onChange(color: string): void;
  error?: boolean;
  helperText?: ReactNode;
  required?: boolean;
  disabled?: boolean;
  size?: "small" | "medium";
  variant?: "outlined" | "filled" | "standard";
  margin?: "dense" | "normal" | "none";
  fullWidth?: boolean;
  swatchSize?: number;
  sx?: SxProps<Theme>;
};

export const ColorInput: FC<ColorInputProps> = (props) => {
  const {
    label,
    placeholder,
    value,
    onChange,
    error,
    helperText,
    required,
    disabled,
    size,
    variant = "outlined",
    margin = "normal",
    fullWidth = true,
    swatchSize = 24,
    sx,
  } = props;
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const [colorFormat, setColorFormat] = useState<ColorFormat>(detectColorFormat(value));

  const handleColorFormatChange = useCallback(() => {
    setColorFormat((c) => {
      switch (c) {
        case "hex":
          onChange(formatIColorToString("rgb", formatStringToIColor("hex", value)));
          return "rgb";
        case "rgb":
          onChange(formatIColorToString("hex", formatStringToIColor("rgb", value)));
          return "hex";
        default:
          return "hex";
      }
    });
  }, [value, onChange]);

  const handleInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const color = e.target.value;
      onChange(color);
    },
    [onChange]
  );

  const handleInputPaste = useCallback(
    (e: ClipboardEvent<HTMLDivElement>) => {
      e.preventDefault();
      const color = e.clipboardData.getData("text");
      const newColorFormat = detectColorFormat(color);
      if (colorFormat !== newColorFormat) {
        setColorFormat(newColorFormat);
      }
      onChange(color);
    },
    [onChange]
  );

  const handleColorPickerChange = useCallback(
    (color: IColor) => {
      onChange(formatIColorToString(colorFormat, color));
    },
    [colorFormat, onChange]
  );

  const openColorPicker = useCallback((e: MouseEvent<HTMLDivElement>) => {
    setAnchorEl(e.currentTarget);
  }, []);

  const closeColorPicker = useCallback(() => {
    setAnchorEl(null);
  }, []);

  return (
    <>
      <TextField
        label={label}
        placeholder={placeholder}
        value={value || ""}
        onChange={handleInputChange}
        onPaste={handleInputPaste}
        error={error}
        helperText={helperText}
        required={required}
        disabled={disabled}
        size={size}
        variant={variant}
        margin={margin}
        fullWidth={fullWidth}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start" sx={{ marginLeft: "8px" }}>
              <Box
                component="div"
                onClick={openColorPicker}
                sx={{
                  height: `${swatchSize}px`,
                  width: `${swatchSize}px`,
                  flexShrink: 0,
                  borderRadius: 1,
                  border: (theme) => `1px solid ${theme.palette.grey[400]}`,
                  backgroundColor: value,
                  cursor: "pointer",
                }}
              />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                onClick={handleColorFormatChange}
                sx={{
                  fontSize: "14px",
                  height: "40px",
                  width: "40px",
                }}
              >
                {colorFormat.toLocaleUpperCase()}
              </IconButton>
            </InputAdornment>
          ),
          sx,
        }}
        sx={{ "& > div": { paddingLeft: "0px" } }}
      />

      <CustomColorPicker
        anchorEl={anchorEl}
        colorFormat={colorFormat}
        value={value}
        onChange={handleColorPickerChange}
        onClose={closeColorPicker}
      />
    </>
  );
};

type CustomColorPickerProps = {
  anchorEl: HTMLDivElement | null;
  colorFormat: ColorFormat;
  value: string | null | undefined;
  onChange: (color: IColor) => void;
  onClose: () => void;
};

const CustomColorPicker: FC<CustomColorPickerProps> = (props) => {
  const { anchorEl, colorFormat, value, onChange, onClose } = props;

  return (
    <Popover
      open={!!anchorEl}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: "center",
        horizontal: "center",
      }}
      transformOrigin={{
        vertical: "center",
        horizontal: "center",
      }}
      sx={{
        "& MuiPaper-root": {
          padding: "8px",
        },
        "& .rcp-body": {
          backgroundColor: "white",
        },
        "& .rcp-saturation": {
          borderRadius: 1,
        },
        "& .rcp-field-input": {
          color: "black",
        },
        "& .rcp-field-label": {
          color: "black",
        },
      }}
    >
      <ColorPicker
        color={formatStringToIColor(colorFormat, value)}
        onChange={onChange}
        hideInput={["hsv", "hex", "rgb"].filter((f) => f !== colorFormat) as (keyof IColor)[]}
      />
    </Popover>
  );
};
